import React from "react";
import {
  FaChalkboardTeacher,
  FaClock,
  FaEuroSign,
  FaInfoCircle,
  FaLayerGroup,
  FaUsers,
} from "react-icons/fa";
import { FiMapPin, FiX } from "react-icons/fi";

import {
  clamp,
  cn,
  fillPercentage,
  getHexColor,
  horairesOf,
  isGroupFull,
  lighten,
  sanitizeHtml,
  teachersOf,
  totalTarif,
} from "./groupPlanningUtils";

function InfoBox({ icon: Icon, title, children }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
        <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Icon />
        </span>
        {title}
      </div>
      <div className="text-sm leading-7 text-slate-600 dark:text-slate-300">
        {children}
      </div>
    </div>
  );
}

export default function GroupModal({ open, onClose, group, dojo }) {
  if (!open || !group) return null;

  const baseColor = getHexColor(group.color_groupe);
  const lightColor = lighten(baseColor, 0.72);
  const percentage = fillPercentage(group);
  const full = isGroupFull(group);
  const horaires = horairesOf(group);
  const teachers = teachersOf(group);
  const total = totalTarif(group);
  const sanitizedInfo = sanitizeHtml(group.info_groupe);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/70 px-3 py-6 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl dark:bg-slate-950"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/85 text-slate-700 shadow-lg backdrop-blur transition hover:text-primary dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-200"
          aria-label="Fermer"
        >
          <FiX className="text-xl" />
        </button>

        <div className="max-h-[92vh] overflow-y-auto">
          <div
            className="relative overflow-hidden p-6 sm:p-8"
            style={{
              background: `linear-gradient(135deg, ${baseColor} 0%, ${lightColor} 100%)`,
            }}
          >
            <div className="absolute -left-16 -top-16 h-48 w-48 rounded-full bg-white/25 blur-3xl" />
            <div className="absolute -bottom-20 -right-20 h-56 w-56 rounded-full bg-white/20 blur-3xl" />

            <div className="relative pr-12 text-slate-950">
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-bold shadow-sm">
                  {dojo?.dojo_nom || "Dojo"}
                </span>

                {group.discipline_groupe && (
                  <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-bold shadow-sm">
                    {group.discipline_groupe}
                  </span>
                )}

                {full && (
                  <span className="rounded-full bg-rose-600 px-3 py-1 text-xs font-bold uppercase text-white shadow-sm">
                    Complet
                  </span>
                )}
              </div>

              <h3 className="mt-5 font-barlow text-3xl font-extrabold uppercase leading-tight sm:text-4xl">
                {group.nom}
              </h3>

              {group.annees_naissance && (
                <p className="mt-2 text-sm font-bold italic">
                  {group.annees_naissance}
                </p>
              )}

              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                <div className="rounded-3xl bg-white/70 p-4 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                    Effectif
                  </p>
                  <p className="mt-1 font-barlow text-2xl font-extrabold">
                    {group.adherentCount || 0}/{group.quota_groupe || 0}
                  </p>
                </div>

                <div className="rounded-3xl bg-white/70 p-4 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                    Remplissage
                  </p>
                  <p className="mt-1 font-barlow text-2xl font-extrabold">
                    {percentage}%
                  </p>
                </div>

                <div className="rounded-3xl bg-white/70 p-4 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                    Tarif annuel
                  </p>
                  <p className="mt-1 font-barlow text-2xl font-extrabold">
                    {total}€
                  </p>
                </div>
              </div>

              <div className="mt-5 h-3 overflow-hidden rounded-full bg-white/50">
                <div
                  className="h-full rounded-full bg-slate-950/75 transition-[width] duration-500"
                  style={{
                    width: `${clamp(percentage, 0, 100)}%`,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="space-y-5 p-6 text-slate-900 dark:text-white sm:p-8">
            {dojo?.dojo_adresse && (
              <div className="flex items-start gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                <FiMapPin className="mt-1 shrink-0 text-primary" />
                <span>
                  <strong className="text-slate-950 dark:text-white">
                    Adresse :
                  </strong>{" "}
                  {dojo.dojo_adresse}
                </span>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <InfoBox icon={FaClock} title="Horaires">
                {horaires.length ? (
                  <ul className="space-y-1">
                    {horaires.map((horaire, index) => (
                      <li key={index}>• {horaire}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="italic opacity-80">Non renseigné.</p>
                )}
              </InfoBox>

              <InfoBox icon={FaEuroSign} title="Tarifs">
                <p>Licence : {group.tarif_licence || 0} €</p>
                <p>Adhésion : {group.tarif_adhesion || 0} €</p>
                <p>Cotisation : {group.tarif_cotisation || 0} €</p>
                <p className="mt-2 font-extrabold text-slate-950 dark:text-white">
                  Total : {total} €
                </p>
              </InfoBox>

              <InfoBox icon={FaChalkboardTeacher} title="Enseignants">
                {teachers.length ? teachers.join(" • ") : "-"}
              </InfoBox>

              <InfoBox icon={FaLayerGroup} title="Discipline">
                {group.discipline_groupe || "-"}
              </InfoBox>

              <InfoBox icon={FaUsers} title="Effectif">
                <p>
                  {group.adherentCount || 0} inscrit(s) /{" "}
                  {group.quota_groupe || 0}
                </p>
                {full && (
                  <span className="mt-2 inline-flex rounded-full bg-rose-600 px-3 py-1 text-xs font-bold uppercase text-white">
                    Groupe complet
                  </span>
                )}
              </InfoBox>
            </div>

            {group.info_groupe && (
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                <div className="mb-4 flex items-center gap-2 font-bold text-slate-950 dark:text-white">
                  <FaInfoCircle className="text-primary" />
                  Informations complémentaires
                </div>

                <div
                  className={cn(
                    "prose prose-slate max-w-none dark:prose-invert",
                    "text-sm leading-7 text-slate-600 dark:text-slate-300",
                    "dark:[&_strong]:!text-white dark:[&_b]:!text-white",
                    "dark:[&_strong]:font-extrabold dark:[&_b]:font-extrabold"
                  )}
                  dangerouslySetInnerHTML={{
                    __html: sanitizedInfo,
                  }}
                />
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-full bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}